import React, { useState, useCallback } from 'react';
import {
  View, Text, StyleSheet, TextInput,
  TouchableOpacity, Platform, Modal,
} from 'react-native';
import { AdminColors } from '../../constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import { useTheme } from '../../../../theme/useTheme';
import { useToast } from '../../../../components/LhToastContainer';
import { createApiClient, getLocalTimestamp, getTimezoneOffsetString } from '../../../../api/apiClient';

interface AddRouteModalProps {
  visible: boolean;
  onClose: () => void;
  /** Pre-fill the location. When omitted, user must enter Location ID and Location Name. */
  locationId?: string | number;
  locationName?: string;
  onSuccess: () => void;
  routeTypeOptions?: { label: string; value: string }[];
}

const AddRouteModal: React.FC<AddRouteModalProps> = ({
  visible, onClose, locationId, locationName, onSuccess, routeTypeOptions = [],
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const toast = useToast();
  const [form, setForm] = useState({
    routeNumber: '',
    routeType: '',
    isActive: true,
    locationIdInput: '',
    locationNameInput: '',
  });
  const [apiError, setApiError] = useState('');
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const selectableLocation = !locationId;

  const hasUnsavedChanges =
    form.routeNumber.trim() !== '' || form.routeType !== '' || !form.isActive ||
    (selectableLocation && (form.locationIdInput.trim() !== '' || form.locationNameInput.trim() !== ''));

  const handleClose = useCallback(() => {
    setForm({ routeNumber: '', routeType: '', isActive: true, locationIdInput: '', locationNameInput: '' });
    setApiError('');
    setShowDiscardConfirm(false);
    onClose();
  }, [onClose]);

  const attemptClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowDiscardConfirm(true);
    } else {
      handleClose();
    }
  }, [hasUnsavedChanges, handleClose]);

  const handleSubmit = useCallback(async () => {
    const resolvedLocationId = locationId ?? form.locationIdInput.trim();
    const resolvedLocationName = locationName ?? form.locationNameInput.trim();
    if (!form.routeNumber.trim() || !form.routeType) {
      setApiError('Please fill in all required fields');
      return;
    }
    if (!resolvedLocationId || !resolvedLocationName) {
      setApiError('Please enter a Location ID and Location Name');
      return;
    }
    setApiError('');
    try {
      const api = createApiClient();
      await api.post(`/perfect-truck/locations/${resolvedLocationId}/routes`, {
        routeNumber: form.routeNumber,
        routeType: form.routeType,
        isActive: form.isActive,
      }, {
        params: {
          locationId: resolvedLocationId,
          locationName: resolvedLocationName,
          clientTimestamp: getLocalTimestamp(),
          clientTimezone: getTimezoneOffsetString(),
        },
      });
      toast.show('Route added successfully', 'success');
      setForm({ routeNumber: '', routeType: '', isActive: true, locationIdInput: '', locationNameInput: '' });
      onClose();
      onSuccess();
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ??
        err?.response?.data?.message ??
        'Failed to add route';
      setApiError(msg);
    }
  }, [form, locationId, locationName, onClose, onSuccess, toast]);

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={attemptClose}
    >
      <View style={styles.overlay}>
        <View style={[styles.content, isDark && { backgroundColor: colors.background.elevated }]}>
          <View style={[styles.header, isDark && { borderBottomColor: colors.border.default }]}>
            <Text style={[styles.title, isDark && { color: colors.text.primary }]}>Add Route Number</Text>
            <TouchableOpacity onPress={attemptClose} activeOpacity={0.7}>
              <Text style={[styles.closeButton, isDark && { color: colors.text.secondary }]}>×</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.body}>
            {!!apiError && (
              <View style={[styles.errorBanner, isDark && { backgroundColor: colors.statusBackground.error, borderLeftColor: colors.status.error }]}>
                <Text style={[styles.errorBannerText, isDark && { color: colors.status.error }]}>{apiError}</Text>
              </View>
            )}

            {/* Location */}
            {selectableLocation ? (
              <>
                <View style={styles.fieldGroup}>
                  <Text style={[styles.label, isDark && { color: colors.text.primary }]}>
                    Location ID <Text style={styles.required}>*</Text>
                  </Text>
                  <TextInput
                    style={[
                      styles.input,
                      isDark && {
                        backgroundColor: colors.input.background,
                        borderColor: colors.input.border,
                        color: colors.text.primary,
                      },
                    ]}
                    placeholder="e.g., 1008"
                    placeholderTextColor={isDark ? colors.text.disabled : AdminColors.textMuted}
                    value={form.locationIdInput}
                    onChangeText={(text) => setForm({ ...form, locationIdInput: text })}
                  />
                </View>
                <View style={styles.fieldGroup}>
                  <Text style={[styles.label, isDark && { color: colors.text.primary }]}>
                    Location Name <Text style={styles.required}>*</Text>
                  </Text>
                  <TextInput
                    style={[
                      styles.input,
                      isDark && {
                        backgroundColor: colors.input.background,
                        borderColor: colors.input.border,
                        color: colors.text.primary,
                      },
                    ]}
                    placeholder="e.g., Portland OR"
                    placeholderTextColor={isDark ? colors.text.disabled : AdminColors.textMuted}
                    value={form.locationNameInput}
                    onChangeText={(text) => setForm({ ...form, locationNameInput: text })}
                  />
                </View>
              </>
            ) : (
              <View style={styles.fieldGroup}>
                <Text style={[styles.label, isDark && { color: colors.text.primary }]}>
                  Location <Text style={styles.required}>*</Text>
                </Text>
                <View style={[styles.disabledField, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
                  <Text style={[styles.fieldText, isDark && { color: colors.text.primary }]}>{locationName}</Text>
                </View>
              </View>
            )}

            {/* Route Number */}
            <View style={styles.fieldGroup}>
              <Text style={[styles.label, isDark && { color: colors.text.primary }]}>
                Route Number <Text style={styles.required}>*</Text>
              </Text>
              <TextInput
                style={[
                  styles.input,
                  isDark && {
                    backgroundColor: colors.input.background,
                    borderColor: colors.input.border,
                    color: colors.text.primary,
                  },
                ]}
                placeholder="e.g., R-109"
                placeholderTextColor={isDark ? colors.text.disabled : AdminColors.textMuted}
                value={form.routeNumber}
                onChangeText={(text) => setForm({ ...form, routeNumber: text })}
              />
            </View>

            {/* Route Type */}
            <View style={styles.fieldGroup}>
              <Text style={[styles.label, isDark && { color: colors.text.primary }]}>
                Route Type <Text style={styles.required}>*</Text>
              </Text>
              {Platform.OS === 'web' ? (
                <select
                  style={{
                    ...(styles.selectWeb as any),
                    ...(isDark
                      ? {
                          backgroundColor: colors.input.background,
                          borderColor: colors.input.border,
                          color: form.routeType ? colors.text.primary : colors.text.disabled,
                        }
                      : {}),
                  }}
                  value={form.routeType}
                  onChange={(e: any) => setForm({ ...form, routeType: e.target.value })}
                >
                  <option value="">Select route type...</option>
                  {routeTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <View style={[styles.selectNative, isDark && { backgroundColor: colors.input.background, borderColor: colors.input.border }]}>
                  <Text style={[styles.fieldText, isDark && { color: form.routeType ? colors.text.primary : colors.text.disabled }]}>
                    {form.routeType
                      ? routeTypeOptions.find(o => o.value === form.routeType)?.label
                      : 'Select route type...'}
                  </Text>
                </View>
              )}
            </View>

            {/* Active */}
            <View style={styles.fieldGroup}>
              <Text style={[styles.label, isDark && { color: colors.text.primary }]}>Active</Text>
              {Platform.OS === 'web' ? (
                <select
                  style={{
                    ...(styles.selectWeb as any),
                    ...(isDark
                      ? {
                          backgroundColor: colors.input.background,
                          borderColor: colors.input.border,
                          color: colors.text.primary,
                        }
                      : {}),
                  }}
                  value={form.isActive ? 'yes' : 'no'}
                  onChange={(e: any) => setForm({ ...form, isActive: e.target.value === 'yes' })}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              ) : (
                <View style={[styles.selectNative, isDark && { backgroundColor: colors.input.background, borderColor: colors.input.border }]}>
                  <Text style={[styles.fieldText, isDark && { color: colors.text.primary }]}>{form.isActive ? 'Yes' : 'No'}</Text>
                </View>
              )}
            </View>
          </View>

          <View style={styles.buttonRow}>
            <TouchableOpacity
              style={[
                styles.button,
                styles.buttonCancel,
                isDark && {
                  backgroundColor: colors.background.surface,
                  borderColor: colors.brand.primary,
                },
              ]}
              onPress={attemptClose}
              activeOpacity={0.7}
            >
              <Text style={[styles.buttonCancelText, isDark && { color: colors.brand.primary }]}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.button,
                styles.buttonSave,
                isDark && {
                  backgroundColor: colors.brand.primary,
                },
              ]}
              onPress={handleSubmit}
              activeOpacity={0.7}
            >
              <Text style={styles.buttonSaveText}>Save</Text>
            </TouchableOpacity>
          </View>

          {/* Discard confirmation overlay */}
          {showDiscardConfirm && (
            <View style={styles.discardOverlay}>
              <View style={[styles.discardCard, isDark && { backgroundColor: colors.background.elevated }]}>
                <Text style={[styles.discardTitle, isDark && { color: colors.text.primary }]}>Discard changes?</Text>
                <Text style={[styles.discardMessage, isDark && { color: colors.text.secondary }]}>
                  You have unsaved changes. Are you sure you want to close without saving?
                </Text>
                <View style={styles.discardButtons}>
                  <TouchableOpacity
                    style={[
                      styles.button,
                      styles.buttonCancel,
                      { flex: 1 },
                      isDark && {
                        backgroundColor: colors.background.surface,
                        borderColor: colors.brand.primary,
                      },
                    ]}
                    onPress={() => setShowDiscardConfirm(false)}
                    activeOpacity={0.7}
                  >
                    <Text style={[styles.buttonCancelText, isDark && { color: colors.brand.primary }]}>Keep Editing</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.button, styles.discardButton, { flex: 1 }]}
                    onPress={handleClose}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.discardButtonText}>Discard</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 12,
    width: '90%',
    maxWidth: 500,
    overflow: 'hidden',
    ...Platform.select({
      web: { boxShadow: '0 8px 32px rgba(0,0,0,0.15)' } as any,
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.15,
        shadowRadius: 16,
        elevation: 8,
      },
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderDefault,
  },
  title: {
    fontSize: 20,
    fontFamily: FontFamily.bold,
    color: AdminColors.textPrimary,
    fontWeight: '600',
  },
  closeButton: {
    fontSize: 32,
    color: AdminColors.textMuted,
    marginTop: -6,
    lineHeight: 24,
  },
  body: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 20,
  },
  fieldGroup: {
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: AdminColors.textPrimary,
    fontWeight: '500',
  },
  required: {
    color: '#DC2626',
  },
  disabledField: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    backgroundColor: AdminColors.bgPage,
    justifyContent: 'center',
    minHeight: 40,
  },
  input: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: AdminColors.textPrimary,
    backgroundColor: AdminColors.bgSurface,
    outlineWidth: 0,
    minHeight: 40,
  } as any,
  selectWeb: {
    width: '100%',
    height: 40,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderStyle: 'solid' as any,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: AdminColors.textPrimary,
    backgroundColor: AdminColors.bgSurface,
    cursor: 'pointer',
    appearance: 'auto' as any,
    boxSizing: 'border-box' as any,
  } as any,
  selectNative: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    backgroundColor: AdminColors.bgSurface,
    justifyContent: 'center',
    minHeight: 40,
  },
  fieldText: {
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: AdminColors.textPrimary,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 16,
    paddingHorizontal: 24,
    paddingBottom: 24,
    justifyContent: 'flex-end',
  },
  button: {
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 28,
    minWidth: 120,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonCancel: {
    borderWidth: 2,
    borderColor: AdminColors.blue700,
    backgroundColor: AdminColors.bgSurface,
  },
  buttonCancelText: {
    fontSize: 15,
    fontFamily: FontFamily.bold,
    color: AdminColors.blue700,
    fontWeight: '700',
  },
  buttonSave: {
    backgroundColor: AdminColors.blue700,
  },
  buttonSaveText: {
    fontSize: 15,
    fontFamily: FontFamily.bold,
    color: '#FFFFFF',
    fontWeight: '700',
  },
  errorBanner: {
    backgroundColor: AdminColors.red75,
    borderLeftWidth: 3,
    borderLeftColor: AdminColors.red400,
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  errorBannerText: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: AdminColors.red400,
    lineHeight: 18,
  },
  discardOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  discardCard: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 10,
    padding: 24,
    width: '100%',
    gap: 12,
  },
  discardTitle: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
    color: AdminColors.textPrimary,
    fontWeight: '700',
  },
  discardMessage: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: AdminColors.textSecondary,
    lineHeight: 20,
  },
  discardButtons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 4,
  },
  discardButton: {
    backgroundColor: AdminColors.red400,
    borderWidth: 0,
  },
  discardButtonText: {
    fontSize: 15,
    fontFamily: FontFamily.bold,
    color: '#FFFFFF',
    fontWeight: '700',
  },
});

export default AddRouteModal;
